import type { OnFilterSheetCloseDetail, OnFilterSheetOpenDetail } from "../filtersheet/FilterSheetTopics";
import type { DresonRuleResolvedEvent } from "../multifiltering/FilterTypes";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { localNavigation } from "../filtersheet/Selectors";
import { Filter } from "../filter/Filter";
import { FilterSection } from "../filter/FilterSection";
import { events } from "../events";
import { eventQBus } from "../types/EventQBus";
import HcFilterSheet, { NBF_SHEET_CHANGE_TRACKING_LABELS, SECOND_LEVEL_CHANGE_TRACKING_LABELS } from "./HcFilterSheet";
import { isMobile } from "../util/Breakpoint";
import { listFeatures, readLegacyLabels, submitActionTrackingFrom } from "../tracking/FeatureTracking";
import { trackNba } from "../nextBestAction/NextBestActionComponent";
import { getActiveVariation } from "../experiment/Experiments";
import { isActive } from "../toggle/Toggle";
import { childElements, getElementById } from "../util/Utils";
import { FilterTitle } from "../filter/FilterTitle";
import { enterSecondLevel, exitSecondLevel, isSecondLevelNbf } from "./HcAllLevelSheet";
import { enterNavigation, exitNavigation, isNavigationActive, LOCAL_NAVIGATION } from "./HcFilterNavigationSheet";
import { onHcSheetBack, onHcSheetClose } from "./hcSheetState.svelte";
import { mount } from "svelte";
import HcSheet from "./HcSheet.svelte";

export { markHcSheetClosed, openHcSheet } from "./hcSheetState.svelte";

function setInitialScrollPosition(scrollOffset?: number) {
  const content = HcFilterSheet.pick()?.sheet?.querySelector<HTMLElement>(".heureka_filterSheet__content");
  if (content) {
    if (scrollOffset) {
      content.dataset.scrollOffset = scrollOffset.toString();
    } else {
      delete content.dataset.scrollOffset;
    }
  }
}

function onFilterSheetOpen() {
  HcFilterSheet.pick()?.open();
}

function addSheetListeners() {
  HcFilterSheet.pick()?.addListeners();
}

function onFilterSheetClose() {
  if (isNavigationActive()) {
    exitNavigation();
  }
  HcFilterSheet.pick()?.close();
}

function onFilterSheetOpenNavigation() {
  memorizeScrollPositionLevel1();
  enterNavigation();
}

function onFilterSheetClosed() {
  setInitialScrollPosition();
}

function onNextBestFilterSheetOpen(event: OnFilterSheetOpenDetail) {
  const { filterId } = event;
  const filter = Filter.filterId(filterId);
  if (filter && filter.form.parentElement) {
    const tracking: Partial<TrackingLabels> = {
      san_FilterFoldoutType: filterId,
      san_FilterFoldoutActivity: "open_nbf",
      ...readLegacyLabels(filter.form),
    };
    filter?.updateChildrenFeatureStatus(true);
    submitActionTrackingFrom(filter.form.parentElement, "open", tracking, listFeatures(["Filter-Overview"]));
  }

  HcFilterSheet.pick()?.open();
  enterSecondLevel(filterId, true);
  eventQBus.emit("ftfind.filterSheet.openedDetail", { filterId });

  Filter.updateChangeTrackingLabels(filterId, NBF_SHEET_CHANGE_TRACKING_LABELS);
}

function onFilterSheetOpenDetail(event: OnFilterSheetOpenDetail) {
  const { filterId } = event;
  memorizeScrollPositionLevel1();
  enterSecondLevel(filterId, false);
  eventQBus.emit("ftfind.filterSheet.openedDetail", { filterId });
  Filter.updateChangeTrackingLabels(filterId, SECOND_LEVEL_CHANGE_TRACKING_LABELS);
}

function memorizeScrollPositionLevel1() {
  const content = HcFilterSheet.pick()?.sheet?.querySelector<HTMLElement>(".heureka_filterSheet__content");
  const scrollOffset = content?.scrollTop;
  setInitialScrollPosition(scrollOffset);
  return scrollOffset;
}

function onFilterSheetCloseDetail(event: OnFilterSheetCloseDetail) {
  const { filterId } = event;

  const wasNbf = isSecondLevelNbf();
  exitSecondLevel();
  eventQBus.emit("ftfind.filterSheet.closedDetail", { filterId });
  if (isActive("HEUREKA_2388_FIX_EMPTY_DETAILSHEET_AFTER_BROWSER_BACK") && wasNbf) {
    return;
  }
  HcFilterSheet.dropPopularValuesOrder();
  HcFilterSheet.scrollToRecentFilter(filterId);
}

function onDresonRuleResolve() {
  HcFilterSheet.declareIfOpen()?.lockContent();
}

function onDresonRuleResolved(data: DresonRuleResolvedEvent) {
  const currentDresonRule = FilterSection.template().currentDresonRule;
  if (data.count == 0 || data.rule === currentDresonRule) {
    setTimeout(() => {
      HcFilterSheet.declareIfOpen()?.unlockContent();
    }, 0);
  }
}

function onDresonRuleResolveAborted() {
  HcFilterSheet.addErrorsOnFilterTitleValues();
  HcFilterSheet.declareIfOpen()?.updateContent().unlockContent();
}

function onFilterSectionLoaded() {
  if (isMobile()) {
    const scrollOffset = memorizeScrollPositionLevel1();
    HcFilterSheet.addLocalNavigationTitleValue();
    HcFilterSheet.addErrorsOnFilterTitleValues();
    HcFilterSheet.initPartialLocking();

    HcFilterSheet.commitPopularValuesOrder();
    const hcFilterSheet = HcFilterSheet.declareIfOpen();
    if (hcFilterSheet) {
      hcFilterSheet.updateContent().unlockContent().setScrollOffset(scrollOffset);
    } else {
      HcFilterSheet.declare()?.removeContent()?.updateContent()?.unlockContent();
    }
  }
}

function onFilterSectionLoadAborted() {
  HcFilterSheet.addInitialLoadError();
  HcFilterSheet.addLocalNavigationTitleValue();
  HcFilterSheet.declareIfOpen()?.updateContent().unlockContent();
}

function onLocalNavigationLoaded() {
  HcFilterSheet.addLocalNavigationTitleValue();
}

function isLocalNavigationPresent() {
  const htmlElements = childElements(localNavigation());
  const localNavigationChildren = htmlElements || [];
  return localNavigationChildren.length > 0;
}

function checkLocalNavigationPresence() {
  if (!isLocalNavigationPresent()) {
    FilterTitle.filterId(LOCAL_NAVIGATION)?.remove();
  }
}

function mountHcSheet() {
  const target = getElementById("hc_sheet_root");
  if (target) {
    mount(HcSheet, { target });
  }
}

export function registerHcFilterSheetHandlers() {
  events.onHcSheetRootLoaded(mountHcSheet);
  onHcSheetClose(() => eventQBus.emit("ftfind.filterSheet.close"));
  onHcSheetBack(() => {
    if (isNavigationActive()) {
      exitNavigation();
      return;
    }
    const filterId = HcFilterSheet.pick()?.sheet?.querySelector<HTMLElement>(".heureka_filterSheet__content")?.dataset
      .activeFilterId;
    eventQBus.emit("ftfind.filterSheet.closeDetail", { filterId: filterId ?? "unknown" });
  });

  eventQBus.on("ftfind.filterSheet.open", onFilterSheetOpen);
  eventQBus.on("ftfind.filterSheet.close", onFilterSheetClose);
  eventQBus.on("ftfind.filterSheet.closed", onFilterSheetClosed);

  if (
    getActiveVariation("e3792", "StatusQuo") !== "StatusQuo" ||
    getActiveVariation("e3911", "StatusQuo") !== "StatusQuo"
  ) {
    eventQBus.on("ftfind.filterSheet.openNextBestFilterDetail", onNextBestFilterSheetOpen);
  }
  eventQBus.on("ftfind.filterSheet.openDetail", onFilterSheetOpenDetail);
  eventQBus.on("ftfind.filterSheet.closeDetail", onFilterSheetCloseDetail);
  eventQBus.on("ftfind.filterSheet.openedDetail", trackNba);

  eventQBus.on("ftfind.filterSheet.openNavigation", onFilterSheetOpenNavigation);

  if (isActive("HEUREKA_2178_FILTER_SECTION_IS_NOT_LOADED")) {
    events.onceFilterSectionInitialLoaded(addSheetListeners);
  } else {
    eventQBus.once("heureka.filterSection.initialLoaded", addSheetListeners);
  }

  /*      */
  eventQBus.on("ftfind.dresonRule.resolve", onDresonRuleResolve);
  eventQBus.on("ftfind.dresonRule.resolved", onDresonRuleResolved);
  eventQBus.on("ftfind.dresonRule.resolveAborted", onDresonRuleResolveAborted);

  /*        */
  events.onFilterSectionLoaded(onFilterSectionLoaded);
  events.onFilterSectionLoadAborted(onFilterSectionLoadAborted);
  eventQBus.on("ftfind.localNavigation.loaded", onLocalNavigationLoaded);
  events.onFilterSectionLoaded(checkLocalNavigationPresence);
}
